import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';

import './OrdersListPage.scss';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

import {
  fetchTasks,
} from '@/features/orders/ordering/services/orderWorkflowService.js';
import {
  genClientId,
} from '@/features/orders/ordering/services/orderService.js';
import {
  getAllStaffData,
} from '@/features/systemManagement/account/accountService.js';
import PaginationComponent from '@/components/Pagination.js';
import SelectInput from '@/components/SelectInput.js';
import TextInput from '@/components/TextInput.js';
import { AdminTaskAction, createActionHandlers } from '../../components/AdminTaskAction/AdminTaskAction.js';
import OrderModal from '../../components/OrderModal/OrderModal.js';

import {
  ISARCHIVE_TYPES,
  MODAL_TYPES,
  ORDER_STATUS_TYPES,
  ROLE_TYPES,
} from '@/constants/constants.js';
import {
  adminOrderStatusOptions,
  companyOrderStatusOptions,
  staffOrderStatusOptions,
} from '@/constants/dropdownOptions.js';

dayjs.extend(utc);
dayjs.extend(timezone);

const ITEMS_PER_PAGE = 8;

const useUserInfo = () => {
  return useMemo(
    () => ({
      role: Number(sessionStorage.getItem('role_id')),
      userid: sessionStorage.getItem('userid'),
    }),
    []
  );
};

const useFilters = () => {
  const [filters, setFilters] = useState({
    status: undefined,
    clientId: '',
    companyName: '',
    clientName: '',
    assignedStaff: undefined,
  });

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      status: undefined,
      clientId: '',
      companyName: '',
      clientName: '',
      assignedStaff: undefined,
    });
  }, []);

  return { filters, updateFilter, clearFilters };
};

const usePagination = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      setCurrentPage(Number(pageParam) - 1);
    } else {
      setCurrentPage(0);
    }
  }, [searchParams]);

  const handlePageChange = useCallback(
    (selected) => {
      const newPage = selected.selected;
      setCurrentPage(newPage);
      setSearchParams({ page: newPage + 1 });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams]
  );

  const resetPage = useCallback(() => {
    setCurrentPage(0);
    setSearchParams({ page: 1 });
  }, [setSearchParams]);

  return { currentPage, handlePageChange, resetPage };
};

const OrdersListPage = () => {
  const { role, userid } = useUserInfo();
  const { filters, updateFilter, clearFilters } = useFilters();
  const { currentPage, handlePageChange, resetPage } = usePagination();

  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [workingOrder, setWorkingOrder] = useState();
  const [activeStaffData, setActiveStaffData] = useState([]);
  const [allStaffData, setAllStaffData] = useState([]);


  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const navigate = useNavigate();

  const getStatusText = useCallback((status) => {
    const reverseOrderStatus = Object.fromEntries(
      Object.entries(ORDER_STATUS_TYPES).map(([key, value]) => [value, key])
    );
    return reverseOrderStatus[status] || '未知状态';
  }, []);

  const getStatusOptions = useCallback((role) => {
    switch (role) {
      case ROLE_TYPES.客服:
        return companyOrderStatusOptions;
      case ROLE_TYPES.制作人员:
        return staffOrderStatusOptions;
      default:
        return adminOrderStatusOptions;
    }
  }, []);

  // 获取人员数据
  const fetchStaffData = useCallback(async () => {
    try {
      const response = await getAllStaffData(ROLE_TYPES.制作人员);
      setAllStaffData(response);
    } catch (error) {
      console.error('获取人员数据失败:', error);
      setAllStaffData([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const filterParams = {
        clientId: filters.clientId,
        companyName: filters.companyName,
        clientName: filters.clientName,
        assignedStaff: filters.assignedStaff,
      };

      const response = await fetchTasks(userid, role, filters.status, filterParams, currentPage + 1, ITEMS_PER_PAGE);

      // 查找工作流进行中的任务
      const foundWorkingOrder = response.data.find(
        (task) => task.status === ORDER_STATUS_TYPES.工作流进行中
      );
      setWorkingOrder(foundWorkingOrder);

      // 转换状态文本
      const convertedData = response.data.map((task) => ({
        ...task,
        statusText: getStatusText(task.status),
      }));

      setTasks(convertedData);
      setTotalTasks(response.total);
    } catch (error) {
      console.error('获取任务列表失败', error);
    } finally {
      setLoading(false);
    }
  }, [userid, role, filters, getStatusText, currentPage]);

  // 初始化获取人员数据
  useEffect(() => {
    if (role === ROLE_TYPES.管理员) {
      fetchStaffData();
    }
  }, [role, fetchStaffData]);

  // 初始化或页码改变时获取数据
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]); 

  // 计算当前页数据 (已废弃，直接使用 tasks)

  const handleTaskClick = (status, client_id) => {
    navigate(`/${status}/${encodeURIComponent(client_id)}`);
  };

  const handleStartBtnClick = (client_id) => {
    const encodedClientId = encodeURIComponent(client_id);
    navigate(`/startWorkFlow/true/${encodedClientId}`);
  };

  const handleCreateTask = async () => {
    const response = await genClientId();
    if (response.status === 200) {
      const client_id = response.data.client_id;
      const status = ORDER_STATUS_TYPES.未提交;
      navigate(`/${status}/${client_id}`);
    }
  };

  const handleTaskResultClick = (client_id) => {
    navigate(`/automation-task-result/${encodeURIComponent(client_id)}`);
  };

  const convertUTCToBeijing = (utcTime) => {
    return dayjs.utc(utcTime).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss');
  };

  const actionHandlers = createActionHandlers(
    userid,
    role,
    workingOrder,
    setModalVisible,
    setModalType,
    setConfirmText,
    handleStartBtnClick,
    handleTaskResultClick,
    fetchData,
    navigate,
    setSelectedOrder
  );

  const TableHeader = () => {
    return (
      <thead className="table-light">
        <tr>
          <th
            className="dashed-border-cell"
            style={{ width: '13%', backgroundColor: '#6dbf58' }}
          >
            客户姓名
          </th>
          <th
            className="dashed-border-cell"
            style={{ width: '13%', backgroundColor: '#6dbf58' }}
          >
            创建公司
          </th>
          <th
            className="dashed-border-cell"
            style={{ width: '10%', backgroundColor: '#6dbf58' }}
          >
            视频标准
          </th>
          <th
            className="dashed-border-cell"
            style={{ width: '10%', backgroundColor: '#6dbf58' }}
          >
            仪式时间
          </th>
          <th
            className="dashed-border-cell"
            style={{ width: '10%', backgroundColor: '#6dbf58' }}
          >
            状态
          </th>
          {role === ROLE_TYPES.管理员 && (
            <th
              className="dashed-border-cell"
              style={{ width: '10%', backgroundColor: '#6dbf58' }}
            >
              分派人员
            </th>
          )}
          <th
            className="dashed-border-cell"
            style={{ width: '10%', backgroundColor: '#6dbf58' }}
          >
            创建时间
          </th>
          <th
            className="dashed-border-cell"
            style={{ width: '20%', backgroundColor: '#6dbf58' }}
          >
            <div className="table-header"> 操作</div>
          </th>
          {role !== ROLE_TYPES.制作人员 && (
            <th
              className="dashed-border-cell"
              style={{
                width: '5%',
                textAlign: 'center',
                backgroundColor: '#6dbf58',
              }}
            >
              删除
            </th>
          )}
        </tr>
      </thead>
    );
  };

  const TableBody = ({ tasks, convertUTCToBeijing, handleTaskClick, role }) => {
    return (
      <tbody>
        {tasks.length > 0 ? (
          tasks.map((task, index) => (
            <tr key={index}>
              <td className="dashed-border-cell">
                <AdminTaskAction
                  task={task}
                  actionHandlers={actionHandlers}
                  role={role}
                  handleTaskClick={handleTaskClick}
                  renderTaskName={true}
                />
              </td>
              <td className="dashed-border-cell">{task.companyName}</td>
              <td className="dashed-border-cell">{task.videoType}</td>
              <td className="dashed-border-cell">{task.ceremonyDate}</td>
              <td className="dashed-border-cell">{task.statusText}</td>
              {role === ROLE_TYPES.管理员 && (
                <td className="dashed-border-cell">
                  <Button
                    onClick={() => {
                      setSelectedOrder(task);
                      setModalType(MODAL_TYPES.人员分配);
                      setConfirmText('确定分配');
                      setModalVisible(true);
                    }}
                  >
                    {task.assigned_staff?.assigned_staff_name
                      ? task.assigned_staff.assigned_staff_name
                      : '未分配'}
                  </Button>
                </td>
              )}
              <td className="dashed-border-cell">
                {convertUTCToBeijing(task.create_time)}
              </td>
              <td className="dashed-border-cell">
                <AdminTaskAction
                  task={task}
                  actionHandlers={actionHandlers}
                  role={role}
                />
              </td>
              {role !== ROLE_TYPES.制作人员 && (
                <td className="dashed-border-cell">
                  {task.isArchive === ISARCHIVE_TYPES.归档 ? (
                    <div className="alert-text">已归档</div>
                  ) : (
                    <div className="operation-container">
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          setSelectedOrder(task);
                          setModalType(MODAL_TYPES.确认删除订单);
                          setConfirmText('确认删除');
                          setModalVisible(true);
                        }}
                        disabled={
                          task.status !== ORDER_STATUS_TYPES.未提交 &&
                          task.status !== ORDER_STATUS_TYPES.文案确认中 &&
                          task.status !== ORDER_STATUS_TYPES.审核中 &&
                          task.status !== ORDER_STATUS_TYPES.资料修改中 &&
                          role === ROLE_TYPES.客服
                        }
                      >
                        <i className="bi bi-trash3"></i>
                      </button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="3" className="text-center">
              暂无任务
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  return (
    <>
      <div className="adminPage-container">
        <div className="orders-header-container">
          <div className="orders-header-title mb-3">我的订单</div>
          <div className="filter-container mb-3">
            <div className="filter-container-conditions filter-container-conditions--compact">
              <div className="select-container select-container--status">
                <div className="conditon-title">订单状态</div>
                <SelectInput
                  name="status"
                  options={getStatusOptions(role)}
                  value={filters.status || ''}
                  onChange={(e) => {
                    updateFilter('status', e.target.value || undefined);
                  }}
                  className="col-md-6"
                  required={false}
                  placeholder="全部"
                />
              </div>
              {role !== ROLE_TYPES.客服 && (
                <div className="select-container select-container--company">
                  <div className="conditon-title">公司名称</div>
                  <TextInput
                    name="companyName"
                    value={filters.companyName}
                    onChange={(e) => {
                      updateFilter('companyName', e.target.value);
                    }}
                    className="col-md-6"
                    required={false}
                    placeholder="请输入公司名称"
                  />
                </div>
              )}
              <div className="select-container select-container--client">
                <div className="conditon-title">客户姓名</div>
                <TextInput
                  name="clientName"
                  value={filters.clientName}
                  onChange={(e) => {
                    updateFilter('clientName', e.target.value);
                  }}
                  className="col-md-6"
                  required={false}
                  placeholder="请输入客户姓名"
                />
              </div>
              {role === ROLE_TYPES.管理员 && (
                <>
                  <div className="select-container select-container--order">
                    <div className="conditon-title">订单号</div>
                    <TextInput
                      name="clientId"
                      value={filters.clientId}
                      onChange={(e) => {
                        updateFilter('clientId', e.target.value);
                      }}
                      className="col-md-6"
                      required={false}
                      placeholder="请输入订单号"
                    />
                  </div>
                  <div className="select-container select-container--staff">
                    <div className="conditon-title">分配人员</div>
                    <SelectInput
                      name="assignedStaff"
                      options={[
                        { value: 'unassigned', label: '未分配' },
                        ...allStaffData.map((staff) => ({
                          value: staff.id,
                          label: staff.companyName,
                        })),
                      ]}
                      value={
                        filters.assignedStaff === ''
                          ? 'unassigned'
                          : filters.assignedStaff || ''
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        let filterValue;
                        if (value === '') {
                          filterValue = undefined; // 全部
                        } else if (value === 'unassigned') {
                          filterValue = ''; // 未分配
                        } else {
                          filterValue = value; // 具体人员ID
                        }
                        updateFilter('assignedStaff', filterValue);
                      }}
                      className="col-md-6"
                      required={false}
                      placeholder="全部"
                    />
                  </div>
                </>
              )}
              <div className="filter-actions d-flex gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  style={{ textWrap: 'nowrap' }}
                  onClick={() => {
                    resetPage(); // 重置到第一页
                    fetchData(); // 手动触发搜索
                  }}
                >
                  搜索
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  style={{ textWrap: 'nowrap', color: '#000' }}
                  onClick={() => {
                    clearFilters();
                    resetPage();
                  }}
                >
                  清空
                </button>
              </div>
            </div>
          </div>
          {role !== ROLE_TYPES.制作人员 && (
            <button
              className="btn btn-primary mb-3"
              style={{ width: '150px' }}
              onClick={handleCreateTask}
            >
              创建新订单
            </button>
          )}
        </div>
        {tasks ? (
          <table className="table table-bordered dashed-border table-striped">
            <TableHeader />
            <TableBody
              tasks={tasks}
              convertUTCToBeijing={convertUTCToBeijing}
              handleTaskClick={handleTaskClick}
              role={role}
            />
          </table>
        ) : (
          <>
            <div>loading</div>
          </>
        )}
        <PaginationComponent
          pageCount={Math.ceil(totalTasks / ITEMS_PER_PAGE)}
          onPageChange={handlePageChange}
          currentPage={currentPage}
        />
      </div>
      <OrderModal
        visible={modalVisible}
        role={role}
        modalType={modalType}
        handleCloseModal={handleCloseModal}
        confirmText={confirmText}
        selectedOrder={selectedOrder}
        fetchData={fetchData}
        workingOrder={workingOrder}
      />
    </>
  );
};

export default OrdersListPage;
